import axios from 'axios';
import { API_BASE_URL } from './tvApi';

/**
 * Screen Profile API wrappers.
 *
 * Profiles are reusable named screen configurations (Landscape TV, Portrait
 * Kiosk, LED Wall, …). A device points at one profile via `profileId`, and
 * AR validation + ScreenPreview + the Screens tab all read from the profile
 * rather than per-device resolution fields.
 *
 * Backend routes live under /api/sumadhura-cms/screen-profiles.
 * See src/controllers/sumadhura.profile.controller.js on the backend for
 * the response shape (profileId / name / icon / physicalType / content /
 * hdmiExpected / rotation / isSystem / status).
 */

const BASE = `${API_BASE_URL}/api/sumadhura-cms/screen-profiles`;

/** List all active profiles. System profiles appear first, then user profiles by name. */
export const listProfiles = async ({ includeArchived = false } = {}) => {
  try {
    const res = await axios.get(BASE, { params: includeArchived ? { includeArchived: 'true' } : {} });
    return res.data; // { success, count, profiles: [...] }
  } catch (error) {
    const msg = error?.response?.data?.message || error?.message || 'Failed to list profiles';
    console.error('[listProfiles]', msg);
    throw new Error(msg);
  }
};

/** Get a single profile by id. */
export const getProfile = async (profileId) => {
  if (!profileId) throw new Error('profileId is required');
  try {
    const res = await axios.get(`${BASE}/${encodeURIComponent(profileId)}`);
    return res.data.profile;
  } catch (error) {
    const msg = error?.response?.data?.message || error?.message || 'Failed to get profile';
    console.error('[getProfile]', msg);
    throw new Error(msg);
  }
};

/**
 * Create a user profile.
 * @param {{name, description?, icon?, physicalType?, content: {width,height}, hdmiExpected?: {width,height}, rotation?}} payload
 */
export const createProfile = async (payload) => {
  try {
    const res = await axios.post(BASE, payload);
    return res.data.profile;
  } catch (error) {
    const msg = error?.response?.data?.message || error?.message || 'Failed to create profile';
    console.error('[createProfile]', msg);
    throw new Error(msg);
  }
};

export const updateProfile = async (profileId, patch) => {
  if (!profileId) throw new Error('profileId is required');
  try {
    const res = await axios.put(`${BASE}/${encodeURIComponent(profileId)}`, patch || {});
    return res.data.profile;
  } catch (error) {
    const msg = error?.response?.data?.message || error?.message || 'Failed to update profile';
    console.error('[updateProfile]', msg);
    throw new Error(msg);
  }
};

export const deleteProfile = async (profileId) => {
  if (!profileId) throw new Error('profileId is required');
  try {
    const res = await axios.delete(`${BASE}/${encodeURIComponent(profileId)}`);
    return res.data;
  } catch (error) {
    // The in-use error is a 409 — surface it as a rich error so the UI can
    // show "this profile is used by N screens" without regex-matching the
    // message.
    const data = error?.response?.data || {};
    const err = new Error(data.message || error.message || 'Failed to delete profile');
    err.inUseCount = data.inUseCount ?? null;
    err.status = error?.response?.status ?? null;
    throw err;
  }
};

/** Clone a profile (typically a system one) into a user-owned copy. */
export const duplicateProfile = async (profileId, { name, createdBy } = {}) => {
  if (!profileId) throw new Error('profileId is required');
  try {
    const res = await axios.post(`${BASE}/${encodeURIComponent(profileId)}/duplicate`, { name, createdBy });
    return res.data.profile;
  } catch (error) {
    const msg = error?.response?.data?.message || error?.message || 'Failed to duplicate profile';
    console.error('[duplicateProfile]', msg);
    throw new Error(msg);
  }
};
